import { SelectContent } from 'components/Wizards/ContentPlanning/SelectContent/SelectContent'
import Styles from './SelectCaptions.module.scss'
import { Header as ContentPlanningHeader } from 'components/Wizards/ContentPlanning/Header/Header'
import { SelectCaptionsProps } from './Types'
import { multiStep } from 'components/Wizards/MultiStepWizard/multiStep'
import { postCountToDaysAndTimes, postCountToNumber, scheduleContent } from 'components/Wizards/utils/postScheduling'
import { useContentPlanningContext } from '../ContentPlanningContext'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { MediaType } from 'components/Wizards/schemas/shared/mediaType'
import { fetchBasicAndByDescription } from 'hooks/useBasicVisualElementsAndVisualElementsByDescription'
import { hasPlaceholderImage } from 'components/Wizards/DesignWizard/utils'
import { useRootStore } from 'stores/RootStore'
import { useNativeLayouts } from 'components/Wizards/hooks/useNativeLayouts'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Archetype } from 'types'

const SelectCaptions = multiStep(
  ({ onBack, onSubmit, initialValues, header, subHeader, inputPlaceholder, nextLabel }: SelectCaptionsProps) => {
    const {
      generatedCaptions,
      selectedCaptions,
      setGeneratedCaptions,
      setSelectedCaptions,
      setScheduledCaptions,
      generateCaptions,
      generateCaptionsMutation,
      generationType,
    } = useContentPlanningContext()
    const { visualElements } = useRootStore()
    const captionCount = initialValues.caption_count
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { media_type: mediaType, width, height, brand_kit_id } = initialValues

    const nativeLayouts = useNativeLayouts(brand_kit_id)
    const nativeLayout = nativeLayouts[generationType as DesignChannel] || { enabled: false, archetypes: [] }

    // for resuming saved drafts, need to generate from initialvalues
    useEffect(() => {
      if (!generateCaptionsMutation?.isLoading && !generateCaptionsMutation?.isError && !generatedCaptions.length) {
        requestAnimationFrame(() => {
          const attempts = postCountToNumber(initialValues.caption_count) / 5
          generateCaptions(initialValues, attempts - 1)
        })
      }
    }, [
      generateCaptionsMutation?.isLoading,
      generatedCaptions.length,
      initialValues,
      generateCaptions,
      generateCaptionsMutation?.isError,
    ])

    const handleSubmit = async () => {
      setIsSubmitting(true)
      const captionsToSchedule = generatedCaptions.filter((c) => selectedCaptions.includes(c.uuid))
      const initialStartDate = dayjs().minute(0).second(0).add(1, 'day')
      let captions = scheduleContent(captionsToSchedule, postCountToDaysAndTimes(captionCount), initialStartDate)

      if (mediaType === MediaType.Design) {
        try {
          if (nativeLayout?.enabled) {
            captions = captions.map((caption, i) => {
              const archetype = nativeLayout.archetypes[i % nativeLayout.archetypes.length]
              const hasReplaceableImage = [
                Archetype.INSTAGRAM_POST_PHOTO_TEXT,
                Archetype.INSTAGRAM_POST_PHOTO_TEXT_LOGO,
                Archetype.PHOTO,
                Archetype.INSTAGRAM_STORY_PHOTO_TEXT,
                Archetype.HEADER_PARAGRAPH_PHOTO,
                Archetype.HEADER_PHOTO,
                Archetype.PHOTO_HEADER_PARAGRAPH,
                Archetype.PHOTO_PARAGRAPH,
                Archetype.PHOTO_HEADER_PARAGRAPH_BOXED,
              ].includes(archetype)
              return {
                ...caption,
                archetype,
                width: nativeLayout.width,
                height: nativeLayout.height,
                hasReplaceableImage,
              }
            })
          } else {
            const templatesResponse = await fetchBasicAndByDescription({
              width,
              height,
              description: '',
              basicLayoutItems: captions.length,
              byDescriptionItems: 0,
              replaceable_has_background: true,
            })
            const templates = templatesResponse.visual_elements
            visualElements.bulkAdd(templates)
            if (!templates.length) throw new Error('No templates found')
            captions = captions.map((caption, index) => ({
              ...caption,
              sourceVisualElementId: templates[index % templates.length].id,
              hasReplaceableImage: hasPlaceholderImage(templates[index % templates.length]),
            }))
          }
        } catch (error) {
          setIsSubmitting(false)
          return
        }
      }

      setScheduledCaptions(captions)
      setIsSubmitting(false)
      return onSubmit({
        scheduled_captions: captions,
        generated_captions: generatedCaptions,
        selected_captions: selectedCaptions,
      })
    }

    const generateMore = () => {
      generateCaptions(initialValues, 0)
    }

    const goBack = () => {
      onBack?.()
      setSelectedCaptions([])
      setGeneratedCaptions([])
    }

    return (
      <SelectContent
        header={<ContentPlanningHeader className={Styles.headerMargin} header={header} subHeader={subHeader} />}
        inputPlaceholder={inputPlaceholder}
        skeletonProps={{
          count: 5,
          rows: 2,
        }}
        onSubmit={() => handleSubmit()}
        goBack={goBack}
        generateMore={generateMore}
        generatedContent={generatedCaptions}
        isLoading={!!generateCaptionsMutation?.isLoading}
        setGeneratedContent={setGeneratedCaptions}
        selectedContent={selectedCaptions}
        setSelectedContent={setSelectedCaptions}
        selectionLimit={postCountToNumber(captionCount)}
        previousLabel="Set Topic"
        nextLabel={nextLabel}
        isSubmitDisabled={isSubmitting}
      />
    )
  },
)

export { SelectCaptions }
